import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import NavbarLoggedInNew from "../Navbar-new/NavbarLoggedInNew";
import styles from "./AssignmentLibrary.module.css";

const DIFFICULTY_CONFIG = {
  E: { label: "Easy", fg: "#1f8a4c" },
  M: { label: "Medium", fg: "#b07d1a" },
  H: { label: "Hard", fg: "#c93c3c" },
};

const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AssignmentLibrary = () => {
  const navigate = useNavigate();

  // State
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ difficulty: "", search: "" });
  const [selectedAssignments, setSelectedAssignments] = useState([]);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    title: "",
    description: "",
    difficulty: "M",
    estimated_time_hours: "",
    tags: "",
  });

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  // Get unique course names
  const uniqueCourseNames = [...new Set(courses.map((c) => c.name))];

  // Get students for selected course name
  const studentsForCourse = selectedCourseName
    ? courses.filter((c) => c.name === selectedCourseName)
    : [];

  // Get modules for selected student's course
  const modules = selectedStudent
    ? courses.find((c) => c.id === parseInt(selectedStudent))?.topics || []
    : [];

  // Fetch assignments
  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.difficulty) params.append("difficulty", filter.difficulty);
      if (filter.search) params.append("search", filter.search);

      const response = await api.get(`api/assignment-library/?${params}`);
      setAssignments(response.data.assignments || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Fetch mentor's course topics on page load
  useEffect(() => {
    const fetchMentorTopics = async () => {
      try {
        const response = await api.get("api/question-library/topics/");
        setCourses(response.data?.courses || []);
      } catch (err) {
        console.error("Failed to fetch topics:", err);
      }
    };
    fetchMentorTopics();
  }, []);

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile || !newAssignment.title) return;

    setUploading(true);
    setUploadResult(null);

    try {
      // Get presigned URL
      const presignedRes = await api.post("api/assignment-library/presigned-url/", {
        filename: uploadFile.name,
        content_type: uploadFile.type || "application/zip",
      });

      const { presigned_url, fields, file_key } = presignedRes.data;

      // Upload to GCS using PUT with signed URL
      await fetch(presigned_url, {
        method: "PUT",
        body: uploadFile,
        headers: {
          "Content-Type": fields["Content-Type"] || uploadFile.type || "application/zip",
        },
      });

      // Create assignment in database
      const createRes = await api.post("api/assignment-library/", {
        title: newAssignment.title,
        description: newAssignment.description,
        difficulty: newAssignment.difficulty,
        estimated_time_hours: newAssignment.estimated_time_hours || null,
        tags: newAssignment.tags ? newAssignment.tags.split(",").map((t) => t.trim()) : [],
        file_key,
        file_name: uploadFile.name,
        file_size: uploadFile.size,
      });

      setUploadResult({
        success: true,
        message: createRes.data.message,
      });

      // Refresh list and reset form
      fetchAssignments();
      setUploadFile(null);
      setNewAssignment({
        title: "",
        description: "",
        difficulty: "M",
        estimated_time_hours: "",
        tags: "",
      });

      setTimeout(() => {
        setShowUpload(false);
        setUploadResult(null);
      }, 2000);
    } catch (err) {
      setUploadResult({
        success: false,
        message: err.response?.data?.error || "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  // Delete assignment
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this assignment?")) return;

    try {
      await api.delete(`api/assignment-library/${id}/`);
      fetchAssignments();
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  };

  // Toggle selection
  const toggleSelect = (a) => {
    setSelectedAssignments((prev) => {
      const exists = prev.includes(a.id);
      if (exists) {
        return prev.filter((id) => id !== a.id);
      }
      return [...prev, a.id];
    });
  };

  const isSelected = (a) => selectedAssignments.includes(a.id);

  // Open assign modal
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
    setSelectedCourseName("");
    setSelectedStudent("");
    setSelectedModule("");
    setAssignResult(null);
  };

  // Assign selected assignments to module
  const handleAssignToModule = async () => {
    if (!selectedModule || selectedAssignments.length === 0) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      const res = await api.post(`api/course/${selectedModule}/library-assignments/assign/`, {
        assignments: selectedAssignments,
      });
      setAssignResult({
        type: "success",
        message: res.data.message || `Assigned ${selectedAssignments.length} assignments`,
      });
      setSelectedAssignments([]);
      fetchAssignments();
      setTimeout(() => {
        setShowAssignModal(false);
        setAssignResult(null);
      }, 1500);
    } catch (err) {
      setAssignResult({
        type: "error",
        message: err.response?.data?.error || "Failed to assign assignments",
      });
    } finally {
      setAssigning(false);
    }
  };

  // Download assignment
  const handleDownload = async (id) => {
    try {
      const res = await api.get(`api/assignment-library/${id}/download/`);
      window.open(res.data.download_url, "_blank");
    } catch (err) {
      alert("Failed to get download URL");
    }
  };

  return (
    <>
      <NavbarLoggedInNew />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Assignment Library</h1>
            <span className={styles.count}>{total} assignments</span>
            <span className={styles.subtitle}>Shared across every course</span>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.uploadBtn}
              onClick={() => setShowUpload(!showUpload)}
            >
              {showUpload ? "Cancel" : "+ Upload Assignment"}
            </button>
          </div>
        </div>

        {/* Upload Panel */}
        {showUpload && (
          <div className={styles.uploadPanel}>
            <div className={styles.uploadForm}>
              <div className={styles.uploadFormRow}>
                <div className={styles.formGroup}>
                  <label>Title *</label>
                  <input
                    type="text"
                    value={newAssignment.title}
                    onChange={(e) =>
                      setNewAssignment({ ...newAssignment, title: e.target.value })
                    }
                    placeholder="Assignment title"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Difficulty</label>
                  <select
                    value={newAssignment.difficulty}
                    onChange={(e) =>
                      setNewAssignment({ ...newAssignment, difficulty: e.target.value })
                    }
                  >
                    <option value="E">Easy</option>
                    <option value="M">Medium</option>
                    <option value="H">Hard</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Est. Time (hours)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={newAssignment.estimated_time_hours}
                    onChange={(e) =>
                      setNewAssignment({
                        ...newAssignment,
                        estimated_time_hours: e.target.value,
                      })
                    }
                    placeholder="e.g. 2.5"
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={newAssignment.description}
                  onChange={(e) =>
                    setNewAssignment({ ...newAssignment, description: e.target.value })
                  }
                  placeholder="Assignment description"
                  rows={2}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={newAssignment.tags}
                  onChange={(e) =>
                    setNewAssignment({ ...newAssignment, tags: e.target.value })
                  }
                  placeholder="python, data-structures, beginner"
                />
              </div>
            </div>
            <div className={styles.uploadDropzone}>
              <div className={styles.uploadIcon}>ZIP</div>
              <div className={styles.uploadContent}>
                <h3 className={styles.uploadTitle}>
                  {uploadFile ? uploadFile.name : "Drop a ZIP file or click to browse"}
                </h3>
                <p className={styles.uploadDesc}>
                  {uploadFile
                    ? formatFileSize(uploadFile.size)
                    : "Upload assignment files as a ZIP archive (max 100MB)"}
                </p>
              </div>
              <div className={styles.uploadActions}>
                <label className={styles.browseBtn}>
                  Browse files
                  <input
                    type="file"
                    accept=".zip,application/zip"
                    hidden
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </label>
              </div>
            </div>
            {uploadFile && newAssignment.title && (
              <div className={styles.uploadFileInfo}>
                <span>Ready to upload: {uploadFile.name}</span>
                <button
                  className={styles.uploadStartBtn}
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "+ Upload Assignment"}
                </button>
              </div>
            )}
            {uploadResult && (
              <div
                className={`${styles.uploadResult} ${
                  uploadResult.success ? styles.success : styles.error
                }`}
              >
                {uploadResult.message}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className={styles.filters}>
          <button
            className={`${styles.filterChip} ${!filter.difficulty ? styles.active : ""}`}
            onClick={() => setFilter({ ...filter, difficulty: "" })}
          >
            All
          </button>
          {Object.entries(DIFFICULTY_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              className={`${styles.filterChip} ${
                filter.difficulty === key ? styles.active : ""
              }`}
              onClick={() => setFilter({ ...filter, difficulty: key })}
            >
              {cfg.label}
            </button>
          ))}
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by title, description..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>

        {/* Table */}
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div className={styles.colCheck}></div>
            <div>ASSIGNMENT</div>
            <div>DIFFICULTY</div>
            <div>SIZE</div>
            <div>VERSION</div>
            <div>USED IN</div>
            <div>ACTIONS</div>
          </div>
          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : assignments.length === 0 ? (
            <div className={styles.empty}>No assignments found</div>
          ) : (
            assignments.map((a) => (
              <div
                key={a.id}
                className={`${styles.tableRow} ${isSelected(a) ? styles.selected : ""}`}
              >
                <div className={styles.colCheck}>
                  <input
                    type="checkbox"
                    checked={isSelected(a)}
                    onChange={() => toggleSelect(a)}
                  />
                </div>
                <div>
                  <div className={styles.assignmentTitle}>{a.title}</div>
                  <div className={styles.assignmentMeta}>
                    {a.file_name} · {a.created_by || "Unknown"}
                  </div>
                </div>
                <div>
                  <span
                    className={styles.diffBadge}
                    style={{ color: DIFFICULTY_CONFIG[a.difficulty]?.fg }}
                  >
                    {DIFFICULTY_CONFIG[a.difficulty]?.label || a.difficulty}
                  </span>
                </div>
                <div>{formatFileSize(a.file_size)}</div>
                <div>v{a.current_version}</div>
                <div className={styles.colUsed}>
                  {a.used_in?.length > 0 ? a.used_in.join(", ") : "-"}
                </div>
                <div className={styles.colActions}>
                  <button
                    className={styles.downloadBtn}
                    onClick={() => handleDownload(a.id)}
                  >
                    Download
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(a.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {selectedAssignments.length > 0 && (
          <div className={styles.footer}>
            <span>{selectedAssignments.length} selected</span>
            <button className={styles.assignBtn} onClick={handleOpenAssignModal}>
              Assign to a module →
            </button>
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <div
            className={styles.modalOverlay}
            onClick={() => setShowAssignModal(false)}
          >
            <div className={styles.assignModal} onClick={(e) => e.stopPropagation()}>
              <h3>Assign {selectedAssignments.length} assignments to module</h3>
              <p>Select a course, student, and module:</p>
              <label className={styles.selectLabel}>Course</label>
              <select
                className={styles.moduleSelect}
                value={selectedCourseName}
                onChange={(e) => {
                  setSelectedCourseName(e.target.value);
                  setSelectedStudent("");
                  setSelectedModule("");
                }}
              >
                <option value="">Select a course...</option>
                {uniqueCourseNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <label className={styles.selectLabel}>Student</label>
              <select
                className={styles.moduleSelect}
                value={selectedStudent}
                onChange={(e) => {
                  setSelectedStudent(e.target.value);
                  setSelectedModule("");
                }}
                disabled={!selectedCourseName}
              >
                <option value="">Select a student...</option>
                {studentsForCourse.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.mentee || "No student assigned"}
                  </option>
                ))}
              </select>
              <label className={styles.selectLabel}>Module</label>
              <select
                className={styles.moduleSelect}
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                disabled={!selectedStudent}
              >
                <option value="">Select a module...</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {assignResult && (
                <div className={`${styles.assignResult} ${styles[assignResult.type]}`}>
                  {assignResult.message}
                </div>
              )}
              <div className={styles.assignModalActions}>
                <button
                  className={styles.modalCancelBtn}
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignResult(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={styles.modalSaveBtn}
                  onClick={handleAssignToModule}
                  disabled={!selectedModule || assigning}
                >
                  {assigning ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AssignmentLibrary;
